import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Popover,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'

export interface DateRange {
  start: string
  end: string
  preset?: string
}

interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  anchorEl?: HTMLButtonElement | null
  open: boolean
  onClose: () => void
}

// Get preset date ranges - exported for use in other components if needed
export const getPresetDateRange = (preset: string): { start: string; end: string } => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const todayStr = today.toISOString().split('T')[0]
  
  switch (preset) {
    case 'today': {
      return { start: todayStr, end: todayStr }
    }
    case 'yesterday': {
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = yesterday.toISOString().split('T')[0]
      return { start: yesterdayStr, end: yesterdayStr }
    }
    case 'thisWeek': {
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      return { start: startOfWeek.toISOString().split('T')[0], end: todayStr }
    }
    case 'lastWeek': {
      const startOfLastWeek = new Date(today)
      startOfLastWeek.setDate(today.getDate() - today.getDay() - 7)
      const endOfLastWeek = new Date(startOfLastWeek)
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6)
      return { start: startOfLastWeek.toISOString().split('T')[0], end: endOfLastWeek.toISOString().split('T')[0] }
    }
    case 'thisMonth': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start: startOfMonth.toISOString().split('T')[0], end: todayStr }
    }
    case 'lastMonth': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
      return { start: startOfLastMonth.toISOString().split('T')[0], end: endOfLastMonth.toISOString().split('T')[0] }
    }
    case 'thisYear': {
      const startOfYear = new Date(today.getFullYear(), 0, 1)
      return { start: startOfYear.toISOString().split('T')[0], end: todayStr }
    }
    case 'lastYear': {
      const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1)
      const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31)
      return { start: startOfLastYear.toISOString().split('T')[0], end: endOfLastYear.toISOString().split('T')[0] }
    }
    case 'allTime':
    default:
      return { start: '', end: '' }
  }
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  anchorEl,
  open,
  onClose,
}) => {
  const [tempStartDate, setTempStartDate] = useState(value.start)
  const [tempEndDate, setTempEndDate] = useState(value.end)

  // Update temp dates when value changes (e.g., from preset selection)
  useEffect(() => {
    setTempStartDate(value.start)
    setTempEndDate(value.end)
  }, [value.start, value.end])

  const handlePresetClick = (preset: string) => {
    const range = getPresetDateRange(preset)
    onChange({ ...range, preset })
  }

  const handleClear = () => {
    onChange({ start: '', end: '', preset: undefined })
    setTempStartDate('')
    setTempEndDate('')
    onClose()
  }

  const handleSubmit = () => {
    onChange({ start: tempStartDate, end: tempEndDate, preset: undefined })
    onClose()
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 580,
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
            Select dates
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <Box
              sx={{
                borderRight: '1px solid',
                borderColor: 'divider',
                pr: 2,
                height: '100%',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                Quick Select
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 0.5,
                  borderRadius: 2,
                  p: 1,
                  background: 'linear-gradient(135deg, rgba(0, 124, 145, 0.05) 0%, rgba(0, 124, 145, 0.02) 100%)',
                }}
              >
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'thisWeek', label: 'This Week' },
                  { key: 'lastWeek', label: 'Last Week' },
                  { key: 'thisMonth', label: 'This Month' },
                  { key: 'lastMonth', label: 'Last Month' },
                  { key: 'thisYear', label: 'This Year' },
                  { key: 'lastYear', label: 'Last Year' },
                  { key: 'allTime', label: 'All Time' },
                ].map((preset) => {
                  const isSelected = value.preset === preset.key
                  return (
                    <Button
                      key={preset.key}
                      fullWidth
                      onClick={() => handlePresetClick(preset.key)}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        px: 2,
                        py: 0.875,
                        borderRadius: 1.5,
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: '0.8125rem',
                        backgroundColor: isSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.6)',
                        color: isSelected ? 'white' : 'text.primary',
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'rgba(0, 124, 145, 0.2)',
                        boxShadow: isSelected ? '0 2px 8px rgba(0, 124, 145, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'rgba(255, 255, 255, 0.9)',
                          borderColor: isSelected ? 'primary.dark' : 'primary.main',
                          transform: isSelected ? 'none' : 'translateX(2px)',
                          boxShadow: isSelected ? '0 2px 8px rgba(0, 124, 145, 0.3)' : '0 2px 4px rgba(0, 124, 145, 0.15)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {preset.label}
                    </Button>
                  )
                })}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={7}>
            <Box sx={{ pl: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                Custom Range
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={tempStartDate}
                  onChange={(e) => {
                    setTempStartDate(e.target.value)
                  }}
                  placeholder="YYYY-MM-DD"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="Enter date manually or use calendar"
                  FormHelperTextProps={{
                    sx: { fontSize: '0.7rem', mt: 0.25, mb: 0 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      py: 1.25,
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={tempEndDate}
                  onChange={(e) => {
                    setTempEndDate(e.target.value)
                  }}
                  placeholder="YYYY-MM-DD"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="Enter date manually or use calendar"
                  FormHelperTextProps={{
                    sx: { fontSize: '0.7rem', mt: 0.25, mb: 0 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      py: 1.25,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={handleSubmit}
                    disabled={!tempStartDate || !tempEndDate}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      py: 1,
                      flex: 1,
                      fontWeight: 600,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '&:disabled': {
                        backgroundColor: 'action.disabledBackground',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={!tempStartDate && !tempEndDate}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      py: 1,
                      flex: 1,
                      fontWeight: 600,
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                      },
                      '&:disabled': {
                        borderColor: 'divider',
                        color: 'text.disabled',
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Popover>
  )
}

export default DateRangePicker
